import React, { useCallback, memo, useMemo } from 'react';
import { View, TouchableOpacity } from 'react-native';
import TouchableHighlight from 'utils/packages/TouchableHighlight';
import Text from 'design-system/Text';
import Logo from 'design-system/Logo';
import Spacer from 'design-system/Spacer';
import IconPlus from 'design-system/icons/IconPlus';
import IconCheck from 'design-system/icons/IconCheck';
import { rem, hitSlop } from 'design-system/values';
import useColors from 'hooks/useColors';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import { useStockMeta, useWatchListStockInfoMap } from '../hooks/selectors';
import { ROW_ITEM_VERTICAL_PADDING, ROW_ITEM_HORIZONTAL_PADDING } from '../constants';
import useAddRemoveSymbolFromWatchList from '../hooks/useAddRemoveSymbolFromWatchList';
import { isWeb } from '../../../constants';
const StockSearchResultWatchList = ({ data, watchListId }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const { navigateDeprecated } = useMainStackNavigation();
    const stockCache = useStockMeta(data.id);
    const [loading, addSymbolToWatchList, removeSymbolFromWatchList] = useAddRemoveSymbolFromWatchList(watchListId);
    const isStockInWatchList = useWatchListStockInfoMap(watchListId, data.id);
    const onPress = useCallback(() => {
        navigateDeprecated('InvestStock', { assetId: data.id });
    }, [data]);
    const onActionBtnPress = () => {
        if (data.symbol) {
            if (isStockInWatchList) {
                removeSymbolFromWatchList(data.symbol, data.id);
                return;
            }
            addSymbolToWatchList(data.symbol, data.id, data.name);
        }
    };
    const borderStyles = useMemo(() => [data.isFirstItem && styles.firstItem, data.isLastItem && styles.lastItem], [data.isFirstItem, data.isLastItem, styles.firstItem, styles.lastItem]);
    return (<View style={[styles.wrapper, borderStyles]}>
      <TouchableHighlight hoverOnLight onCard={isWeb} onPress={onPress} activeOpacity={1} style={isWeb ? undefined : borderStyles} underlayColor={colors.background.underlay}>
        <View style={styles.container}>
          <Logo R44 uri={stockCache?.iconUrl} resizeMode="contain"/>
          <Spacer w={16}/>
          <View style={styles.row}>
            <View style={styles.flex}>
              <Text Text-16 flex style={styles.stockName} numberOfLines={1}>
                {data.name}
              </Text>
              {!!data.symbol && <Text TextThin-14 Gray flex>{`$${data.symbol}`}</Text>}
            </View>
            <View style={styles.btnContainer}>
              <TouchableOpacity hitSlop={hitSlop} disabled={loading || !data.symbol} onPress={onActionBtnPress}>
                {isStockInWatchList ? (<IconCheck alt color={colors.elements.brand} width={16}/>) : (<IconPlus color={colors.text.secondary}/>)}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </TouchableHighlight>
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    wrapper: {
        marginHorizontal: rem(16),
        backgroundColor: colors.cards.onDark,
        ...(isWeb && { paddingHorizontal: rem(8) }),
    },
    container: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: rem(ROW_ITEM_HORIZONTAL_PADDING),
        paddingVertical: rem(ROW_ITEM_VERTICAL_PADDING),
        // On web the card is padded by 8 and each row by 8, so the hover highlight sits inside the card
        ...(isWeb && { paddingHorizontal: rem(8) }),
    },
    stockName: {
        flexShrink: 1,
        marginRight: rem(16),
    },
    flex: {
        flex: 1,
    },
    row: {
        flex: 1,
        flexDirection: 'row',
    },
    btnContainer: {
        justifyContent: 'center',
    },
    firstItem: {
        borderTopLeftRadius: rem(16),
        borderTopRightRadius: rem(16),
        ...(isWeb && { paddingTop: rem(8) }),
    },
    lastItem: {
        borderBottomLeftRadius: rem(16),
        borderBottomRightRadius: rem(16),
        ...(isWeb && { paddingBottom: rem(8) }),
    },
}));
export default memo(StockSearchResultWatchList);
